import ModalBase from '@/components/base/ModalBase';
import {
  type Payment,
  methodLabels,
  paymentStatusLabels,
  paymentStatusColors,
} from '@/mocks/superAdminPayments';

interface PaymentDetailModalProps {
  payment: Payment;
  onClose: () => void;
  onMarkPaid: (p: Payment) => void;
  onRetry: (p: Payment) => void;
  onRefund: (p: Payment) => void;
  onCancel: (p: Payment) => void;
  onNavigate: (path: string) => void;
}

type PaymentMethod = Payment['method'];

function formatCurrency(n: number): string {
  return `$${n.toFixed(2)}`;
}

const methodIconMap: Record<PaymentMethod, string> = {
  stripe: 'ri-bank-card-line',
  paypal: 'ri-paypal-line',
  square: 'ri-apps-2-line',
  manual: 'ri-user-settings-line',
};

const methodChip: Record<PaymentMethod, string> = {
  stripe: 'bg-emerald-50 text-emerald-600',
  paypal: 'bg-sky-50 text-sky-600',
  square: 'bg-indigo-50 text-indigo-600',
  manual: 'bg-foreground-100 text-foreground-600',
};

export default function PaymentDetailModal({
  payment: p,
  onClose,
  onMarkPaid,
  onRetry,
  onRefund,
  onCancel,
  onNavigate,
}: PaymentDetailModalProps) {
  const canPay = p.status === 'pending' || p.status === 'failed';
  const canRetry = p.status === 'failed';
  const canRefund = p.status === 'paid' || p.status === 'pending';
  const canCancel = p.status === 'pending' || p.status === 'manual_review' || p.status === 'failed';

  return (
    <ModalBase
      open={true}
      onClose={onClose}
      title={p.reference}
      subtitle={p.radioName}
      size="md"
    >
      <div className="space-y-4">
        {/* Amount + method + status */}
        <div className="flex items-center justify-between bg-background-100 rounded-xl p-4">
          <div>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${paymentStatusColors[p.status]}`}>
              {paymentStatusLabels[p.status]}
            </span>
            <p className="text-foreground-800 text-2xl font-bold mt-1">{formatCurrency(p.amount)}</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-medium ${methodChip[p.method]}`}>
            <i className={`${methodIconMap[p.method]} text-[10px]`}></i>
            {methodLabels[p.method]}
          </span>
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Radio</p>
            <p className="text-foreground-800 text-xs font-semibold">{p.radioName}</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Frecuencia</p>
            <p className="text-foreground-800 text-xs font-semibold">{p.frequency}</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Plan</p>
            <p className="text-foreground-800 text-xs font-semibold">{p.planName}</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Fecha</p>
            <p className="text-foreground-800 text-xs font-semibold">{p.date}</p>
          </div>
        </div>

        {/* Licencia */}
        {p.annualLicense && (
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Licencia anual</p>
            <p className="text-foreground-800 text-xs font-semibold">{p.annualLicense}</p>
          </div>
        )}

        {/* Extras */}
        {p.extras && p.extras.length > 0 && (
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-1.5">Extras</p>
            <div className="flex flex-wrap gap-1.5">
              {p.extras.map((e, i) => (
                <span key={i} className="px-2 py-0.5 rounded-full bg-background-200 text-foreground-600 text-[10px] font-medium">{e}</span>
              ))}
            </div>
          </div>
        )}

        {/* Invoice & Contract links */}
        {(p.invoiceId || p.contractId) && (
          <div className="grid grid-cols-2 gap-2">
            {p.invoiceId && (
              <div className="bg-background-100 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Invoice</p>
                <button onClick={() => { onClose(); onNavigate('/sa/invoices'); }} className="text-foreground-800 text-xs font-semibold font-mono hover:text-foreground-600 transition-colors cursor-pointer">
                  {p.invoiceId}
                </button>
              </div>
            )}
            {p.contractId && (
              <div className="bg-background-100 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Contrato</p>
                <button onClick={() => { onClose(); onNavigate('/sa/contracts'); }} className="text-foreground-800 text-xs font-semibold font-mono hover:text-foreground-600 transition-colors cursor-pointer">
                  {p.contractId}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Procesador */}
        {p.processor && (
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Procesador</p>
            <p className="text-foreground-800 text-xs">{p.processor}</p>
          </div>
        )}

        {/* Description */}
        {p.description && (
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Descripción</p>
            <p className="text-foreground-700 text-xs">{p.description}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-background-200">
          {canPay && (
            <button onClick={() => { onMarkPaid(p); onClose(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-check-line text-sm"></i>Marcar pagado
            </button>
          )}
          {canRetry && (
            <button onClick={() => { onRetry(p); onClose(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-refresh-line text-sm"></i>Reintentar
            </button>
          )}
          {canRefund && (
            <button onClick={() => { onRefund(p); onClose(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-refund-2-line text-sm"></i>Reembolsar
            </button>
          )}
          {canCancel && (
            <button onClick={() => { onCancel(p); onClose(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-400 text-white text-xs font-medium hover:bg-foreground-500 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-forbid-line text-sm"></i>Cancelar
            </button>
          )}
          <button onClick={() => { onClose(); onNavigate('/sa/billing'); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-bank-card-line text-sm"></i>Billing
          </button>
        </div>
      </div>
    </ModalBase>
  );
}